import CursorText from "components/CursorText";
import Player from "@vimeo/player";
import "yet-another-react-lightbox/styles.css";
import { useEffect, useRef } from "react";

function DynamicVideobox({ imageUrl, videoUrl, onEnded, playing, onPlayClick }) {
  const containerRef = useRef(null);
  const playerRef = useRef(null);

  // Ref mirrors the latest `playing` prop so async callbacks always see current value
  const playingRef = useRef(playing);
  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  const resolvedUrl = videoUrl?.length > 10
    ? videoUrl
    : `https://vimeo.com/${videoUrl}`;

  // Create / recreate player whenever videoUrl changes
  useEffect(() => {
    if (!containerRef.current || !videoUrl) return;

    if (playerRef.current) {
      playerRef.current.destroy();
      playerRef.current = null;
    }

    const player = new Player(containerRef.current, {
      url: resolvedUrl,
      responsive: true,
      controls: true,
      autoplay: false,
      muted: false,
    });

    player.ready().then(() => {
      playerRef.current = player;

      // Force iframe to fill container
      const iframe = containerRef.current?.querySelector("iframe");
      if (iframe) {
        iframe.style.position = "absolute";
        iframe.style.inset = "0";
        iframe.style.width = "100%";
        iframe.style.height = "100%";
      }

      // Use ref — not closure — so we always read the latest playing value
      if (playingRef.current) player.play();
    });

    player.on("ended", () => {
      if (onEnded) onEnded();
    });

    return () => {
      player.destroy();
      playerRef.current = null;
    };
  }, [videoUrl]);

  // Respond to playing prop changes after player is ready
  useEffect(() => {
    if (!playerRef.current) return;
    if (playing) {
      playerRef.current.play();
    } else {
      playerRef.current.pause();
    }
  }, [playing]);

  return (
    <div
      className="player-wrapper-small"
      style={{ position: "relative", width: "100%", paddingTop: "56.25%" }}
    >
      {/* @vimeo/player mounts here */}
      <div
        ref={containerRef}
        style={{
          position: "absolute",
          inset: "0",
          width: "100%",
          height: "100%",
        }}
      />

      {/* Thumbnail — sits on top until play is clicked */}
      {!playing && (
        <>
          <CursorText />
          <img
            src={imageUrl}
            alt="Video Thumbnail"
            style={{
              position: "absolute",
              inset: "0",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              cursor: "none",
              zIndex: 2,
            }}
            onClick={onPlayClick}
          />
        </>
      )}
    </div>
  );
}

export default DynamicVideobox;
